import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown } from "lucide-react";

function AckRule({ rule, read, onAck }) {
  const [open, setOpen] = useState(!read);
  return (
    <div className={`border ${read ? "border-[var(--ff-border)]" : "border-[var(--ff-red)]/60"} bg-[var(--ff-bg)]`} data-testid={`onb-rule-${rule.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`onb-rule-toggle-${rule.id}`}>
        {read ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0" /> : <span className="w-4 h-4 border border-[var(--ff-red)] shrink-0" />}
        <span className="font-display flex-1">{rule.title}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pl-11">
          <div className="text-sm text-[var(--ff-text-dim)] whitespace-pre-wrap leading-relaxed mb-4">{rule.body}</div>
          {!read && (
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`onb-rule-ack-${rule.id}`}>
              <input type="checkbox" onChange={(e) => e.target.checked && onAck(rule)} /> I have read and understand this policy
            </label>
          )}
        </div>
      )}
    </div>
  );
}

export default function PolicyAckStep({ onProgress }) {
  const [sections, setSections] = useState(null);
  const [acked, setAcked] = useState({});

  useEffect(() => {
    Promise.all([api.get("/policies"), api.get("/policies/me")]).then(([p, me]) => {
      setSections(p.data.sections);
      setAcked(me.data.acked || {});
    }).catch(() => toast.error("Couldn't load policies"));
  }, []);

  const rules = (sections || []).flatMap((s) => s.rules);
  const isRead = (r) => (acked[r.id] || 0) >= r.version;
  const readCount = rules.filter(isRead).length;
  useEffect(() => { if (sections) onProgress(readCount, rules.length); }, [readCount, rules.length, sections]); // eslint-disable-line react-hooks/exhaustive-deps

  const ack = async (rule) => {
    try {
      await api.post("/policies/ack", { rule_ids: [rule.id] });
      setAcked((a) => ({ ...a, [rule.id]: rule.version }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!sections) return <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading policies…</div>;
  const pct = rules.length ? Math.round((readCount / rules.length) * 100) : 100;
  return (
    <div className="space-y-6" data-testid="onb-policy-step">
      <div>
        <div className="flex justify-between font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] mb-2">
          <span>Policies read</span><span data-testid="onb-policy-progress">{readCount}/{rules.length}</span>
        </div>
        <div className="h-1.5 bg-[var(--ff-border)]"><div className="h-full bg-[var(--ff-red)] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
      {rules.length === 0 && <div className="text-sm text-[var(--ff-text-dim)]">No policies are published yet — you&apos;re clear to continue.</div>}
      {sections.filter((s) => s.rules.length).map((s) => (
        <div key={s.id} className="space-y-2">
          <div className="label-eyebrow text-[var(--ff-red)]">{s.title}</div>
          {s.rules.map((r) => <AckRule key={`${r.id}-${r.version}`} rule={r} read={isRead(r)} onAck={ack} />)}
        </div>
      ))}
    </div>
  );
}
